import { useEffect, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";

/**
 * Controlled PWA update UX (2026-10-01).
 *
 * Listens for the `wow:update-waiting` event fired by the SW registration in
 * index.js. Two paths, both keep the user logged in (the session lives in
 * localStorage and is never cleared on update):
 *
 *  - atBoot === true  → a new version was waiting when the app launched. Show
 *    a brief "Updating to the latest version…" splash, then apply + reload so
 *    the user starts the day current ("verifying latest update" behaviour).
 *  - atBoot === false → the update landed while someone is actively using the
 *    app. NEVER force a reload mid-task — show a quiet "Update ready" pill they
 *    can tap when convenient (it also applies automatically on next launch).
 */
export default function UpdateManager() {
  const [applying, setApplying] = useState(false);
  const [showPill, setShowPill] = useState(false);

  // Safety net: the "Updating…" overlay relies on the SW
  // SKIP_WAITING → controllerchange → reload handshake. If that never
  // completes (no waiting-worker reference, controllerchange never fires on
  // some Android/Zebra WebViews, etc.) the spinner would otherwise hang
  // forever — the exact "it gets stuck on that screen" report. Once we start
  // applying, arm a hard-reload fallback so the newest shell always loads.
  // localStorage session is untouched, so the user stays signed in.
  useEffect(() => {
    if (!applying) return undefined;
    const t = setTimeout(() => {
      try { window.location.reload(); } catch (_) { window.location.href = "/"; }
    }, 4500);
    return () => clearTimeout(t);
  }, [applying]);

  useEffect(() => {
    const onWaiting = (e) => {
      const atBoot = !!(e && e.detail && e.detail.atBoot);
      if (atBoot) {
        setApplying(true);
        // Let the overlay paint for a beat, then apply → controllerchange reload.
        setTimeout(() => { try { window.__wowApplyUpdate && window.__wowApplyUpdate(); } catch (_) {} }, 450);
      } else {
        setShowPill(true);
      }
    };
    window.addEventListener("wow:update-waiting", onWaiting);
    return () => window.removeEventListener("wow:update-waiting", onWaiting);
  }, []);

  const applyNow = () => {
    setShowPill(false);
    setApplying(true);
    setTimeout(() => { try { window.__wowApplyUpdate && window.__wowApplyUpdate(); } catch (_) {} }, 300);
  };

  if (applying) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-sm"
        data-testid="update-applying-overlay"
      >
        <ArrowClockwise size={34} weight="bold" className="text-emerald-400 animate-spin" />
        <p className="mt-4 text-sm font-semibold text-white">Updating to the latest version…</p>
        <p className="mt-1 text-xs text-slate-400">You'll stay signed in.</p>
      </div>
    );
  }

  if (showPill) {
    return (
      <button
        type="button"
        onClick={applyNow}
        data-testid="update-ready-pill"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-2 rounded-full
                   bg-slate-900 text-white pl-4 pr-3 py-2.5 shadow-xl ring-1 ring-white/10
                   hover:bg-slate-800 active:scale-95 transition-all duration-150"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, sans-serif" }}
      >
        <span className="text-sm font-semibold">A new version is ready</span>
        <span className="flex items-center gap-1 text-xs font-bold text-emerald-300">
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </span>
      </button>
    );
  }

  return null;
}
